/**
 * Listens on `root` for `type` and calls `handler` with the element matching
 * `selector` that the event started on or inside: the target itself, or its
 * nearest matching ancestor. One listener on `document` serves every matching
 * element, including those rendered after it was added.
 *
 * Pass `options` as to `addEventListener`. Events that do not bubble, such as
 * `mouseenter`, reach a listener on `document` only in the capture phase.
 *
 * Each call adds a new listener, so a module that delegates at boot guards
 * against being initialized twice. Returns a function that removes the
 * listener.
 */
export function delegate<K extends keyof DocumentEventMap>(
  root: Document | Element,
  type: K,
  selector: string,
  handler: (event: DocumentEventMap[K], matched: Element) => void,
  options?: boolean | AddEventListenerOptions
): () => void {
  const listener = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const matched = target.closest(selector);
    if (!matched || !root.contains(matched)) return;

    handler(event as DocumentEventMap[K], matched);
  };

  root.addEventListener(type, listener, options);
  return () => root.removeEventListener(type, listener, options);
}
